import { useSelector } from 'react-redux';

import { useQuery } from '@tanstack/react-query';

import type { FleetProducts } from '@cobliteam/common-types';

import { GraphQLCameraFleetFlagRequester, isWebDataSuccess } from '@cobliteam/api-lib';

import type { AppState } from '@cobliteam/store';

const graphQLCameraFleetFlagRequester = new GraphQLCameraFleetFlagRequester();
const FLEET_FLAG_QUERY_KEY = 'camera-fleet-flag';

const useCameraFleetFlag = () => {
  const loginWebData = useSelector((state: AppState) => state.login.loginWebData);

  const getCameraFleetFlag = (enabled = true) => {
    const fleetId = isWebDataSuccess(loginWebData) ? loginWebData.data.fleet.id : undefined;

    return useQuery<Boolean>({
      queryKey: [FLEET_FLAG_QUERY_KEY, fleetId],
      queryFn: graphQLCameraFleetFlagRequester.getCameraFleetFlag,
      retry: false,
      enabled,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: Number.POSITIVE_INFINITY,
    });
  };

  const getFleetPremiumPackageFlag = (enabled = true) => {
    const fleetId = isWebDataSuccess(loginWebData) ? loginWebData.data.fleet.id : undefined;

    return useQuery<FleetProducts | undefined, unknown, boolean>({
      queryKey: [FLEET_FLAG_QUERY_KEY, fleetId, 'FLEET_PRODUCTS_FLAG'],
      queryFn: graphQLCameraFleetFlagRequester.getFleetProducts,
      select: (data: FleetProducts | undefined) => {
        return !!data?.products.some((product) => product.name.toLowerCase() === 'premium');
      },
      retry: false,
      enabled,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: Number.POSITIVE_INFINITY,
    });
  };

  return {
    FLEET_FLAG_QUERY_KEY,
    getCameraFleetFlag,
    getFleetPremiumPackageFlag,
  };
};

export default useCameraFleetFlag;
